import { useEffect, useRef, useState } from "react";
import { DefaultValues, FieldValues, useForm } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";

function useHandleParams<F extends FieldValues>({
  defaultValues,
}: {
  defaultValues: DefaultValues<F>;
}) {
  const searchRef = useRef<HTMLInputElement>(null);

  const methods = useForm<F>({
    defaultValues,
  });
  const form = methods.watch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInit, setIsInit] = useState(false);
  const pathname = useLocation().pathname;

  // set form on init
  useEffect(() => {
    const form = Object.fromEntries(searchParams.entries()) as F;
    methods.reset({
      ...defaultValues,
      ...form,
      ...(defaultValues.page
        ? { page: +form.page || +defaultValues.page }
        : {}),
    });
    if (form.searchText) {
      if (searchRef?.current) {
        searchRef.current.value = form.searchText;
      }
    }
    setIsInit(true);
  }, []);

  // set params on form change
  useEffect(() => {
    const filteredParams = Object.fromEntries(
      Object.entries(form).filter(([_, value]) => !!value && value !== "all"),
    );
    if (
      Object.keys(filteredParams).length &&
      JSON.stringify(Object.fromEntries(searchParams.entries())) !==
        JSON.stringify(filteredParams)
    ) {
      setSearchParams(filteredParams, { replace: true });
    }
  }, [JSON.stringify(form)]);

  useEffect(() => {
    if (!isInit) {
      return;
    }

    methods.reset(defaultValues);
    if (searchRef?.current) {
      searchRef.current.value = "";
    }

    const filteredParams = Object.fromEntries(
      Object.entries(defaultValues).filter(
        ([_, value]) => !!value && value !== "all",
      ),
    );
    if (
      Object.keys(filteredParams).length &&
      JSON.stringify(Object.fromEntries(searchParams.entries())) !==
        JSON.stringify(filteredParams)
    ) {
      setSearchParams(filteredParams);
    }
  }, [pathname]);

  // reinitialize params and form on click back
  useEffect(() => {
    if (!isInit) {
      return;
    }

    if (!searchParams.size) {
      methods.reset(defaultValues);
      if (searchRef?.current) {
        searchRef.current.value = "";
      }

      const filteredParams = Object.fromEntries(
        Object.entries(defaultValues).filter(
          ([_, value]) => !!value && value !== "all",
        ),
      );
      if (
        Object.keys(filteredParams).length &&
        JSON.stringify(Object.fromEntries(searchParams.entries())) !==
          JSON.stringify(filteredParams)
      ) {
        setSearchParams(filteredParams, { replace: true });
      }
    }
  }, [searchParams]);

  return { methods, searchRef };
}

export default useHandleParams;
